import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {Box, Card, MenuItem, Popover, Skeleton, TextField, Typography} from "@mui/material";
import Api from "../../../../../../../core/services/api/api";
import Utils, {formatMoney} from "../../../../../../../core/services/utils/utils";
import Slider, {ResponsiveSlider} from "../../../../../../components/base/slider/slider";
import {Add, ChevronLeft, ChevronRight, Remove} from "@mui/icons-material";
import Button from "../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import AddToQuoteModal from "../modal/add_to_quote";
import {
    PartFormComplianceCard,
    PartFormQualificationErrorIndicator,
    PartFormQualificationLoadingIndicator,
    usePartFormQualification,
    usePartFormQualificationGate,
} from "../../../../../../features/part-form-qualification";
import IconButton from "../../../../../../components/base/button/icon_button";
import ReactImageMagnify from "react-image-magnify";
import PartPricesModal from "../modal/prices";
import ImagesPreviewModal from "../../../../../../components/dialogs/images-preview";
import TryAgain from "../../../../../../components/app-specific/try-again";
import Divider from "@mui/material/Divider";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import useWindowViewportWidth from "../../../../../../hooks/use-window/viewport-width";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import ElementIds from "../../../../../../../core/models/element-ids";
import CustomerAccountingAndSearchesDetails from "../../../../../../components/app-specific/customer-accounting-and-searches-details";
import useQuery from "../../../../../../hooks/use-query";
import DeliveryModeChip from "../../../../../../components/app-specific/delivery-mode-chip";
import {useHistory} from "react-router-dom";
import CustomerAutocomplete from "../../../../../../components/text-fields/autocomplete/customer-autocomplete";
import axios from "axios";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {stringifyUrl} from "query-string";
import {ProductOtherPartnerAvailabilityCard, ProductWarehouseCard} from "../../../../../../components/app-specific/product-cards/product-warehouse-card";
import {Alert} from "@mui/lab";
import {ProductTitleText} from "../../../products/single";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import TagChipsContent from "../../../../../../components/app-specific/chips-contents/tag-chips-content";
import {ReactComponent as ImagePlaceholder} from "../../../../../../../assets/images/dropzone/image-placeholder.svg";
import EnvService from "../../../../../../../core/services/env";
import {generateBusinessName, generateWarehouseName} from "../../../../../../../core/services/utils/name-generator";


const NULL_VALUE_PLACEHOLDER = "--";

const LookupProductInformationViewBasicSection = ({
                                                      partNumber,
                                                      customer,
                                                      setPart: setPartProps,
                                                      setLoading: setLoadingProps,
                                                      id,
                                                      className,
                                                      showPrices,
                                                      setShowPrices,
                                                      removeSection,
                                                  }) => {
    const history = useHistory();
    const historyRef = useRef(history);
    historyRef.current = history;
    const [query, setQuery] = useQuery();
    const operatorAccessLevels = useAccessLevels();

    const isMounted = useIsMounted();
    const [part, setPart] = useState();
    const [partLoading, setPartLoading] = useState(true);

    const [availabilityLoading, setAvailabilityLoading] = useState(true);
    const [availability, setAvailability] = useState({warehouses: [], otherPartners: [],});
    const [accessedWarehouses, setAccessedWarehouses] = useState([]);

    const [prices, setPrices] = useState();
    const [pricesLoading, setPricesLoading] = useState(true);

    const [previewImages, setPreviewImages] = useState({open: false, images: []});
    const [activeImageIndex, setActiveImageIndex] = useState();

    const [loadingCurrentCustomerPrice, setLoadingCurrentCustomerPrice] = useState(false);
    const [currentCustomerPriceAndPriceLevel, setCurrentCustomerPriceAndPriceLevel] = useState({});
    const [compareCustomers, setCompareCustomers] = useState([]);
    const [addingCompareCustomer, setAddingCompareCustomer] = useState(false);

    const [loadingVariantInformation, setLoadingVariantInformation] = useState(true);
    const [variantInformation, setVariantInformation] = useState();

    const [shippingCompanies, setShippingCompanies] = useState([]);

    const viewportWidth = useWindowViewportWidth();
    /** @type {React.MutableRefObject<import('react-slick').default>}*/
    const imageSliderRef = useRef();
    /** @type {React.MutableRefObject<import('react-slick').default>}*/
    const priceLevelSliderRef = useRef();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * Gets the sales person's warehouses and access levels.
     * @type {(function(): Promise<void>)}
     */
    const getSalesPersonWarehousesAndAccessLevels = useCallback(async () => {
        const response = await Api.getSalesPersonProfile()
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setAccessedWarehouses(response.data?.warehouseAccess ?? []);
        }
    }, [isMounted])

    /**
     * Fetches the sales person's warehouses and access levels with the first render.
     */
    useEffect(() => {
        void getSalesPersonWarehousesAndAccessLevels();
    }, [getSalesPersonWarehousesAndAccessLevels])

    /**
     * Fetches the shipping companies from the server and if the result of the api is successful, sets the state.
     * @type {(function(): Promise<void>)}
     */
    const getShippingCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setShippingCompanies(response?.data ?? []);
        }
    }, [isMounted])

    /**
     * As soon as the component mounts, it fetches the shipping companies.
     */
    useEffect(() => {
        void getShippingCompanies();
    }, [getShippingCompanies])

    /**
     * Fetches the product's key information from the server.
     * @type {(function(string): Promise<void>)|*}
     */
    const getProductKeyInformation = useCallback(async (partNo) => {
        if (!partNo)
            return;
        const response = await Api.getProductKeyInformation(partNo);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPart(prevState => ({
                ...prevState,
                title: response.data.title ?? "",
                deliveryMode: response.data.deliveryMode,
            }));
        }
    }, [isMounted])

    /**
     * Fetches the basic information of the part including its images, and if the result of the api is successful,
     * sets the state.
     * @type {(function(cancelToken?): Promise<unknown>)}
     */
    const getPartInformation = useCallback(async (cancelToken) => {
        setPartLoading(true);
        setLoadingProps(true);
        const response = await Api.getPartInformation(partNumber, undefined, cancelToken);
        if (!isMounted())
            return;
        setPartLoading(false);
        setLoadingProps(false);
        const product = (response?.data ?? {});
        if (response?.resultFlag) {
            setPart(Object.fromEntries(Object.entries(product)?.map(([key, value]) => key === "gallery" ? [key, value.sort((a, b) => a.orderNumber - b.orderNumber)] : [key, value]) ?? []));
            setActiveImageIndex(product?.gallery?.length ? 0 : -1)
            setPartProps(product);
        }
        if (!response?.resultFlag || !response?.data) {
            removeSection(id);
            historyRef.current.goBack();
            return undefined;
        }
        return product;
    }, [partNumber, setLoadingProps, removeSection, setPartProps, isMounted, id])

    /**
     * Fetches the part's prices.
     * @type {(function(*): Promise<void>)}
     */
    const getPartPrices = useCallback(async (partId, cancelToken) => {
        setPricesLoading(true);
        if (operatorAccessLevels.packages.sales.pricesInLookup) {
            const [response, priceLevelDict] = await Promise.all([
                Api.getPartLivePriceLevels(partId, cancelToken),
                getPriceLevelDictionary(),
            ])
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setPrices(response?.data
                        ?.sort((a, b) => a.priceLevel.levelIndex - b.priceLevel.levelIndex)
                        ?.map(e => ({
                            ...e,
                            priceLevel: {
                                ...e.priceLevel,
                                title: priceLevelDict?.find(p => p.priceLevelId === e.priceLevel.id)?.title ?? e.priceLevel?.title,
                            },
                        }))
                    ?? [])
            }
        }
        if (!isMounted())
            return;
        setPricesLoading(false);
    }, [getPriceLevelDictionary, isMounted, operatorAccessLevels.packages.sales.pricesInLookup])

    /**
     * Fetches the part's availability from the server and if the result of the api is successful, sets the state.
     * @type {(function(*): Promise<void>)}
     */
    const getAvailabilityPrices = useCallback(async (partners, cancelToken) => {
        const prices = [];
        for (const partner of partners) {
            // TODO: call api to fetch the price of a partner for the product.
            console.log(cancelToken);
            const response = {
                resultFlag: true,
                data: [{partNumber: partNumber, originalPrice: 900, price: 300,}],
            }
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                prices.push({
                    partnerName: partner.partnerName,
                    price: response?.data?.price,
                })
            }
        }
        setAvailability(prevState => ({
            ...prevState,
            otherPartners: prevState.otherPartners?.map(e => ({
                ...e,
                price: prices.find(p => p.partnerName === e.name)?.price ?? 0,
            })) ?? {},
        }));
    }, [partNumber, isMounted])

    /**
     * Fetches the warehouse schedules from the server and if the result of the api is successful, sets the state.
     * @type {(function(*): Promise<void>)|*}
     */
    const getWarehouseSchedules = useCallback(async (warehouseIds) => {
        const response = await Api.getPartnerOperationHours({warehouseIds: warehouseIds});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const allItems = response.data || [];
            if (!allItems.length) return;

            // Group by warehouseId
            const schedules = {};
            for (const item of allItems) {
                if (!schedules[item.warehouseId])
                    schedules[item.warehouseId] = [];
                schedules[item.warehouseId].push(item);
            }
            setAvailability(prevState => ({
                ...prevState,
                schedules: schedules
            }));
        }
    }, [isMounted])

    /**
     * Fetches the part's availability from the server and if the result of the api is successful, sets the state.
     * @type {(function(): Promise<void>)}
     */
    const getPartAvailability = useCallback(async () => {
        setAvailabilityLoading(true);
        if (operatorAccessLevels.packages.bizInventory.availabilityInLookup) {
            const [availabilityResponse, warehousesResponse] = await Promise.all([
                await Api.getProductAvailabilitiesV2({partNumbers: [partNumber]}),
                await Api.getOperatorWarehouses(),
            ])
            if (!isMounted())
                return;
            if (availabilityResponse?.resultFlag && warehousesResponse?.resultFlag) {
                const data = availabilityResponse.data?.[0];
                const otherPartners = data?.partners?.filter(e => !e.isMainPartner) ?? [];
                setAvailability(prevState => ({
                    ...prevState,
                    warehouses: data?.localWarehouses?.map(e => ({localWarehouse: e, spireWarehouse: warehousesResponse.data?.find(l => l.id === e.warehouseId)})) ?? [],
                    otherPartners: otherPartners,
                }))
                if (operatorAccessLevels.packages.sales.warehouseSchedules) {
                    const allWarehouseIds = Array.from(new Set(data?.partners?.map(e => e.warehouses)?.flat()?.map(w => w.id) ?? [])).filter(Boolean);
                    if (!!allWarehouseIds.length) {
                        void getWarehouseSchedules(allWarehouseIds);
                    }
                }
                // TODO: include price fetching for other partners.
            }
        }
        setAvailabilityLoading(false);
    }, [operatorAccessLevels.packages.bizInventory.availabilityInLookup, operatorAccessLevels.packages.sales.warehouseSchedules, partNumber, isMounted, getWarehouseSchedules])

    /**
     * Fetches the price of the part for the selected customer.
     * @type {(function(): Promise<void>)}
     */
    const getCurrentCustomerPrice = useCallback(async () => {
        setLoadingCurrentCustomerPrice(true);
        if (operatorAccessLevels.packages.sales.pricesInLookup) {
            const response = await Api.getPriceForCustomer({
                partNumbers: [partNumber],
                customerId: customer?.id,
            });
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                const data = response.data?.find(e => e.partNo === partNumber)
                setCurrentCustomerPriceAndPriceLevel({
                    price: data?.price ?? 0,
                    originalPrice: data?.originalPrice ?? data?.price ?? 0,
                    priceLevelTitle: prices?.find(e => e.priceLevel.id === data?.priceLevelId)?.priceLevel?.title ?? '--',
                });
            } else {
                setCurrentCustomerPriceAndPriceLevel({});
            }
        }
        if (!isMounted())
            return;
        setLoadingCurrentCustomerPrice(false);
    }, [isMounted, customer?.id, partNumber, prices, operatorAccessLevels.packages.sales.pricesInLookup])

    /**
     * With each change in the customer:
     * - Resets the compare customers array
     */
    useEffect(() => {
        setCompareCustomers([]);
        if (!customer?.id)
            return;
        void getCurrentCustomerPrice();
    }, [customer?.id, getCurrentCustomerPrice]);

    /**
     * With each change in the user id or the part number:
     * - Sets the loading of the current customer price to true
     */
    useLayoutEffect(() => {
        if (customer?.id) {
            setLoadingCurrentCustomerPrice(true);
        }
    }, [customer?.id, partNumber,]);


    /**
     * Fetches all the variants of a product from the server.
     * @type {(function(*, *): Promise<*[] | undefined>)}
     */
    const getProductVariantsInformation = useCallback(async (cancelToken) => {
        if (!operatorAccessLevels.packages.lookup.productVariants) {
            setLoadingVariantInformation(false);
            return;
        }
        setLoadingVariantInformation(true);
        const response = await Api.getProductVariant({partNumber: partNumber}, cancelToken)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setVariantInformation(response?.data);
        }
        setLoadingVariantInformation(false);
        return response?.data;
    }, [isMounted, partNumber, operatorAccessLevels.packages.lookup.productVariants])


    /**
     * Fetches the part's basic information, availabilities and prices from the server.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPartInformation = useCallback(async (cancelTokenSource) => {
        void getProductVariantsInformation(cancelTokenSource?.token);
        void getPartAvailability(cancelTokenSource?.token);
        const partInformation = await getPartInformation(cancelTokenSource?.token);
        if (!partInformation?.id)
            return
        void getPartPrices(partInformation.id, cancelTokenSource?.token);
        void getProductKeyInformation(partInformation.partNo);
    }, [getProductVariantsInformation, getPartAvailability, getPartInformation, getPartPrices, getProductKeyInformation])

    /**
     * Gets the parts' basic information from the server.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPartInformation(cancelTokenSource);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [fetchPartInformation]);

    /**
     * Opens the preview images dialog for viewing of the images
     * @type {(function(): void)}
     */
    const openPreviewImages = useCallback(() => {
        setPreviewImages({
            open: true,
            images: part?.gallery?.map((e, i) => ({
                src: e.url,
                alt: e.fileName,
                first: i === activeImageIndex,
            })) ?? []
        });
    }, [part?.gallery, activeImageIndex])

    /**
     * Slides the image slider left or right based on the received offset
     * @type {(function(*): void)}
     */
    const changeImage = useCallback((offset) => {
        if (offset > 0) {
            imageSliderRef.current.slickNext();
            return;
        }
        imageSliderRef.current.slickPrev();
    }, [])

    /**
     * Slides the price level slider left or right based on the received offset
     * @type {(function(*): void)}
     */
    const slidePriceLevels = useCallback((offset) => {
        if (offset > 0) {
            priceLevelSliderRef.current.slickNext();
            return;
        }
        priceLevelSliderRef.current.slickPrev();
    }, [])

    /**
     * Binds the customer to the search token
     * @type {(function(*, *): Promise<void>)|*}
     */
    const bindCustomerToSearchToken = useCallback(async (searchToken, customer) => {
        await Api.connectNexusSearchToCustomer({
            searchToken: searchToken,
            customerId: customer.id,
        });
    }, [])

    /**
     * Sets the customer in the url
     * @type {(function(*, any): void)}
     */
    const changeCustomer = useCallback((_, customer) => {
        setQuery(prevState => ({...prevState, customer: JSON.stringify(customer)}), {replace: true});
        if (query.searchToken) {
            void bindCustomerToSearchToken(query.searchToken, customer);
        }
    }, [setQuery, bindCustomerToSearchToken, query.searchToken])

    /**
     * Removes the customer from the url and replaces the url with the new url
     * @type {(function(): void)}
     */
    const removeCustomer = useCallback(() => {
        setQuery(prevState => ({...prevState, customer: undefined}), {replace: true});
    }, [setQuery])


    /**
     * Fetches the price of the part for the selected customer and then adds the customer to the compare customers
     * @type {(function(*, any): Promise<void>)}
     */
    const getCompareCustomerPrice = useCallback(async (_, customer) => {
        setAddingCompareCustomer(true);
        const response = await Api.getPriceForCustomer({
            partNumbers: [partNumber],
            customerId: customer.id,
        });
        if (!isMounted())
            return;
        setAddingCompareCustomer(false);
        if (response?.resultFlag) {
            const data = response.data?.find(e => e.partNo === partNumber)
            setCompareCustomers(prevState => [
                ...prevState,
                {
                    customerId: customer?.id,
                    customerName: customer?.name,
                    price: data?.price ?? 0,
                    originalPrice: data?.originalPrice ?? data?.price ?? 0,
                    priceLevelTitle: prices?.find(e => e.priceLevel.id === data?.priceLevelId)?.priceLevel?.title ?? '--',
                }
            ]);
        }
    }, [partNumber, prices, isMounted])


    const inLargeViews = useMemo(() => ['lg', 'xl', 'xxl'].includes(viewportWidth), [viewportWidth]);

    const imageSection = (
        <Col xs={12} {...!inLargeViews && {lg: 6, xl: 7}} className='mb-3'>
            {
                partLoading
                    ? <div className='loading part-images'>
                        <div/>
                    </div>
                    : (
                        <>
                            <div className={'position-absolute w-100 h-100 z-index-100'}>
                                {
                                    activeImageIndex !== -1 && !partLoading && !!part?.gallery?.length &&
                                    <div onClick={openPreviewImages}>
                                        <ReactImageMagnify
                                            className={'w-100 h-100 zoom-image'}
                                            smallImage={{
                                                alt: 'image of part',
                                                isFluidWidth: true,
                                                src: part?.gallery[activeImageIndex]?.url ?? ' '
                                            }}
                                            enlargedImagePosition={'over'}
                                            largeImage={{
                                                src: part?.gallery[activeImageIndex]?.url ?? ' ',
                                                width: window.innerWidth / 2,
                                                height: window.innerWidth / 2,
                                            }}
                                            imageStyle={{
                                                maxHeight: '40vh',
                                                width: '100%',
                                                objectFit: 'contain',
                                                height: '100%',
                                                visibility: 'hidden',
                                            }}
                                            enlargedImageStyle={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                            }}
                                            lensStyle={{
                                                borderRadius: '10px',
                                            }}
                                            isEnlargedImagePortalEnabledForTouch
                                            shouldUsePositiveSpaceLens
                                            enlargedImageContainerClassName={'part-zoom-image-container'}
                                        />
                                    </div>
                                }
                            </div>
                            <div className={'look-up-single-image-gallery'}>
                                {
                                    part?.gallery?.length > 1 &&
                                    <IconButton
                                        color={"primary"}
                                        className={'slider-arrow prev'}
                                        onPressed={() => changeImage(-1)}
                                    >
                                        <ChevronLeft className={'icon small'}/>
                                    </IconButton>
                                }
                                {
                                    activeImageIndex !== -1 &&
                                    <Slider
                                        ref={imageSliderRef}
                                        afterChange={(index) => setActiveImageIndex(index)}
                                        className={'gallery-slider'}
                                        autoplay={false}
                                        infinite={true}
                                    >
                                        {part?.gallery?.map((image) =>
                                            (<div className='part no-shadow h-100' key={image.id}>
                                                <div className='img single h-100'>
                                                    <img
                                                        src={Utils.encodeFileUrl(image.url)}
                                                        alt={image.fileName}
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        className={'disable-downloading-image'}
                                                    />
                                                </div>
                                            </div>)
                                        )}
                                    </Slider>
                                }
                                {
                                    activeImageIndex === -1 &&
                                    <div className='part no-shadow h-100  d-flex m-auto'>
                                        <div className='img single h-100'>
                                            <ImagePlaceholder className={'disable-downloading-image'} style={{maxWidth: '100%', minWidth: 400, minHeight: 400}}/>
                                        </div>
                                    </div>
                                }
                                {
                                    part?.gallery?.length > 1 &&
                                    <IconButton
                                        color={"primary"}
                                        className={'slider-arrow next'}
                                        onPressed={() => changeImage(1)}
                                    >
                                        <ChevronRight className={'icon small'}/>
                                    </IconButton>
                                }
                            </div>
                        </>
                    )
            }
        </Col>
    );

    const variantCatalog = useMemo(() => {
        if (!variantInformation?.properties?.length || !variantInformation?.variantParts?.length) {
            return {groups: [], variants: []};
        }

        const filteredProperties = variantInformation.properties.filter(property => {
            return variantInformation.variantParts.some(variantPart => {
                const {formatted: value} = Utils.resolveVariantPropertyDisplay(
                    property.industryPropertyTypeId,
                    variantPart.properties,
                    property.id,
                    property.nullReplacement,
                );
                return value !== null && value !== undefined && value !== "";
            })
        });

        const groups = (filteredProperties ?? []).map((property, idx) => ({
            ...property,
            order: property.order ?? idx,
        }));

        const variants = (variantInformation.variantParts ?? []).map(variantPart => ({
            partNo: variantPart.partNo,
            variantDetails: Object.fromEntries(
                (filteredProperties ?? []).map(property => {
                    let {raw: rawValue, formatted: formattedValue} = Utils.resolveVariantPropertyDisplay(
                        property.industryPropertyTypeId,
                        variantPart.properties,
                        property.id,
                        property.nullReplacement,
                    );

                    if (formattedValue === null || formattedValue === undefined || formattedValue === "") {
                        formattedValue = NULL_VALUE_PLACEHOLDER;
                        rawValue = null;
                    }
                    return [property.id, {raw: rawValue, formatted: formattedValue}];
                })
            ),
        }));

        return {groups, variants};
    }, [variantInformation]);

    const variantDetails = useMemo(() => {
        if (!variantCatalog.variants.length || !part?.partNo) {
            return undefined;
        }
        return variantCatalog.variants.find(v => v.partNo === part.partNo)?.variantDetails;
    }, [variantCatalog, part?.partNo]);

    const groupedVariantOptions = useMemo(() => {
        // Guard clauses for incomplete data
        if (!variantCatalog.groups.length || !variantCatalog.variants.length) {
            return [];
        }

        // Sort groups by order and initialize result shape
        const groupsSorted = [...variantCatalog.groups]
            .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

        const variantsList = variantCatalog.variants ?? [];

        // Build options top-down: each group's options are constrained by all prior selections
        return groupsSorted.map((group, idx) => {
            // Determine prior groups in the hierarchy
            const priorGroups = groupsSorted.slice(0, idx);

            // Filter variants to those matching all prior selected values (if selected)
            const filtered = variantsList.filter(v => {
                const details = v?.variantDetails ?? {};
                return priorGroups.every(pg => {
                    const selected = variantDetails?.[pg.id];
                    return selected ? details?.[pg.id]?.raw === selected?.raw : true;
                });
            });

            // Collect unique values for the current group from the filtered variants
            const seen = new Set();
            const options = [];
            for (const v of filtered) {
                const value = v?.variantDetails?.[group.id];
                const rawValue = value?.raw ?? null;
                if (!seen.has(rawValue)) {
                    seen.add(rawValue);
                    options.push({id: rawValue, title: value?.formatted ?? NULL_VALUE_PLACEHOLDER});
                }
            }

            // Sort options: numerical values are ordered naturally, string values alphabetically
            options.sort((a, b) => {
                if (a.id === null && b.id === null) return 0;
                if (a.id === null) return 1;
                if (b.id === null) return -1;
                return String(a.id).localeCompare(String(b.id), undefined, {numeric: true});
            });

            return {...group, variants: options};
        });
    }, [variantCatalog, variantDetails]);

    /**
     * Navigates to the valid variant combination (closest match) given a user's selection.
     * Strategy:
     * 1) Preserve prior selections when they remain valid with the new pick.
     * 2) For any invalid/unknown groups down the hierarchy, pick the first valid option
     *    based on the partial selections chosen so far.
     */
    const navigateToVariant = useCallback((currentDetails, variantGroup, pickedVariant) => {
        // Guard: must have variants and hierarchy to proceed
        if (!variantCatalog?.variants?.length || !variantCatalog?.groups?.length) return;

        // Sort hierarchy by configured order
        const hierarchy = [...variantCatalog.groups].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

        // Target is "existing selections + the new user choice"
        // Note: we just need raw values for resolution
        const target = {};
        for (const [key, val] of Object.entries(currentDetails ?? {})) {
            target[key] = val?.raw;
        }
        target[variantGroup.id] = pickedVariant.id; // pickedVariant.id is already raw from groupedVariantOptions

        // Helper: from a partial selection, derive all allowed values for a given group
        const getAllowedValues = (partial, groupId) => {
            const seen = new Set();
            const values = [];
            for (const v of (variantCatalog?.variants ?? [])) {
                const details = v?.variantDetails ?? {};
                // Check if this variant matches the partial selection
                let matches = true;
                for (const [k, val] of Object.entries(partial)) {
                    if (val === undefined) continue;
                    if (details[k]?.raw !== val) {
                        matches = false;
                        break;
                    }
                }
                if (!matches) continue;

                const value = details[groupId];
                const rawValue = value?.raw ?? null;
                if (!seen.has(rawValue)) {
                    seen.add(rawValue);
                    values.push(rawValue);
                }
            }
            return values;
        };

        // Build a valid combination top-down
        const resolved = {};
        for (const g of hierarchy) {
            const allowed = getAllowedValues(resolved, g.id);
            if (!allowed.length) {
                // No valid path from here; bail out gracefully
                return;
            }
            const desired = target[g.id];
            resolved[g.id] = allowed.includes(desired) ? desired : allowed[0];
        }

        // Find the exact variant for the resolved combination
        const match = (variantCatalog?.variants ?? []).find(v => {
            const details = v?.variantDetails ?? {};
            return hierarchy.every(g => details[g.id]?.raw === resolved[g.id]);
        });

        const nextPartNo = match?.partNo;
        if (!nextPartNo || nextPartNo === part?.partNo) return;

        history.push(
            stringifyUrl(
                {url: routeFunctions.lookup.single(nextPartNo), query: query},
                {arrayFormat: 'bracket'}
            )
        );
    }, [variantCatalog, part?.partNo, history, query]);

    const partTags = useMemo(() => (part?.tags ?? []).map(tag => ({
        id: tag.id,
        name: tag.name,
        iconFileName: tag.iconFileName ?? tag.iconInfo?.url,
        industryId: tag.industryId,
    })).filter(tag => !!(tag.name ?? '').trim()), [part?.tags]);

    const variantsSection = (
        <>
            <Box sx={{
                width: '100%',
                marginBottom: (!loadingVariantInformation && groupedVariantOptions?.length > 0 && variantInformation.variantParts?.length > 1) || loadingVariantInformation ? 3 : 0,
                paddingInline: 2, display: 'flex', flexDirection: 'column', gap: 1
            }}>
                {
                    loadingVariantInformation &&
                    Array.from({length: 3}).map((_, i) => (
                        <Box key={i} sx={{marginBottom: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Skeleton sx={{transform: 'none'}} variant='text' width={100} height={20}/>
                            <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
                                {
                                    Array.from({length: 8}).map((_, j) => (
                                        <Skeleton key={j} sx={{transform: 'none'}} variant={'rounded'} width={90} height={40}/>
                                    ))
                                }
                            </Box>
                        </Box>
                    ))
                }
                {
                    // The guard of variantParts.length > 1 is to prevent the variant catalog from rendering when there is only one variant part.
                    !loadingVariantInformation && groupedVariantOptions?.length > 0 && variantInformation.variantParts?.length > 1 &&
                    groupedVariantOptions.map(variantGroup => (
                        <Box key={variantGroup.id} sx={{marginBottom: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant='body2' fontWeight={600} sx={{color: 'secondary.main'}}>
                                {variantGroup.title ?? ""}
                            </Typography>
                            <Box sx={{display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
                                {variantGroup.variants?.map(variant => (
                                    <Card
                                        key={variant.id}
                                        variant={'outlined'}
                                        onClick={() => navigateToVariant(variantDetails, variantGroup, variant)}
                                        sx={{
                                            paddingInline: 2,
                                            paddingBlock: 1,
                                            pointerEvents: 'none',
                                            ...(!loadingVariantInformation && {
                                                cursor: 'pointer',
                                                pointerEvents: 'all',
                                            }),
                                            borderColor: variantDetails?.[variantGroup.id]?.raw === variant.id ? 'primary.main' : 'divider',
                                            backgroundColor: (theme) => variantDetails?.[variantGroup.id]?.raw === variant.id ? `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.selectedOpacity})` : 'background.paper',
                                            color: variantDetails?.[variantGroup.id]?.raw === variant.id ? 'primary.main' : 'text.secondary',
                                        }}
                                    >
                                        <Typography variant='body2' fontWeight={600} sx={{color: 'inherit'}}>
                                            {variant.title}
                                        </Typography>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    ))
                }
            </Box>
        </>
    );

    const basicInfoSection = (
        <Col xs={12} {...!inLargeViews && {lg: 6, xl: 5}}>
            {
                partLoading
                    ? <div className='loading part-info mb-4'>
                        <div/>
                        <div/>
                    </div>
                    : <Row className='no-gutters mb-4 w-100'>
                        <Col xs={12} className='mb-2 mt-4 mt-lg-0 d-flex no-gutters align-items-start justify-content-start'>
                            <ProductTitleText
                                mainTextVariant={'h6'}
                                secondaryTextVariant={'body1'}
                                loading={partLoading}
                                partNo={part?.partNo ?? ""}
                                title={part?.title ?? ""}
                                showPartNoPopover={false}
                                showCopy={!!part?.partNo}
                            />
                        </Col>
                        <Col xs={12}>
                            <p className='text-sm font-weight-semi-bold text-color-2 text-left text-wrap'>
                                {part?.parentCategory?.title ?? ""} {part?.parentCategory?.title && part?.category?.title ? '>' : ''} {part?.category?.title ?? ""}
                            </p>
                        </Col>
                        {
                            !partLoading &&
                            <Col xs={12} className={'mt-2 mb-2'}>
                                <DeliveryModeChip deliveryMode={part?.deliveryMode}/>
                            </Col>
                        }
                        {
                            partTags.length > 0 &&
                            <Col xs={12} className={'mt-2'}>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {
                                        partTags.map(tag => (
                                            <Chips
                                                key={tag.id}
                                                contentType={ChipsContentType.element}
                                                type={ChipsTypes.none}
                                                iconPosition={ChipsIconPosition.start}
                                                buttonProps={{size: 'small'}}
                                                content={<TagChipsContent tag={tag} useIcon/>}
                                            />
                                        ))
                                    }
                                </Box>
                            </Col>
                        }
                        {
                            !!query?.codes?.length &&
                            <Col xs={12} className={'h-100 mt-3'}>
                                <div
                                    className='look-up-single-part-code-wrapper'>
                                    {
                                        [...new Set(query?.codes ?? [])]?.map(e => (
                                            <span key={e} className={'part-code'}>{e}</span>
                                        ))
                                    }
                                </div>
                            </Col>
                        }
                    </Row>
            }
        </Col>
    )

    const warehouseCardPartInfo = useMemo(() => ({
        partId: part?.id,
        partNo: part?.partNo,
        isSerializable: part?.isSerializable,
        cost: part?.cost,
    }), [part]);

    const currentCustomerInfo = useMemo(() => ({
        customerId: customer?.id,
        price: currentCustomerPriceAndPriceLevel.price,
        originalPrice: currentCustomerPriceAndPriceLevel.originalPrice,
        name: customer?.name,
    }), [customer?.id, currentCustomerPriceAndPriceLevel.price, currentCustomerPriceAndPriceLevel.originalPrice, customer?.name]);

    /**
     * Sets the schedule for a specific warehouse.
     * @type {(function(*, *): void)|*}
     */
    const setSchedule = useCallback((warehouseId, updater) => {
        setAvailability(prevState => {
            const found = prevState.schedules?.[warehouseId];
            const updatedSchedules = typeof updater === 'function' ? updater(found ?? []) : updater;
            return {
                ...prevState,
                schedules: {
                    ...(prevState.schedules ?? {}),
                    [warehouseId]: updatedSchedules
                }
            }
        })
    }, [])

    const availabilitySection = (
        <Col xs={12} {...!inLargeViews && {lg: 6, xl: 5}}>
            {
                availabilityLoading
                    ? <div className='loading part-warehouse mb-5'>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                    </div>
                    : <div className={'d-flex flex-column'}>
                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                            {
                                availability?.warehouses?.map(warehouse => (
                                    <ProductWarehouseCard
                                        viewOnly
                                        key={warehouse.localWarehouse.warehouseId ?? warehouse.spireWarehouse?.code}
                                        localWarehouse={warehouse.localWarehouse}
                                        spireWarehouse={warehouse.spireWarehouse}
                                        shippingCompanies={shippingCompanies}
                                        customerInfo={currentCustomerInfo}
                                        partInfo={warehouseCardPartInfo}
                                        schedule={availability.schedules?.[warehouse.localWarehouse.warehouseId]}
                                        setSchedule={setSchedule}
                                    />
                                ))
                            }
                        </Box>
                        {
                            !!availability.otherPartners?.length &&
                            <Box sx={{position: 'relative', marginBlock: 4}}>
                                <Divider
                                    absolute
                                    sx={{
                                        borderStyle: "dashed",
                                        borderColor: 'text.disabled',
                                        borderBottomWidth: 2,
                                        paddingInline: 3,
                                    }}
                                />
                                <Typography
                                    variant={'body2'}
                                    color={'tertiary.main'}
                                    fontWeight={600}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -65%)',
                                        backgroundColor: 'background.paper',
                                        paddingInline: 2,
                                    }}
                                >
                                    Other Partners
                                </Typography>
                            </Box>
                        }
                        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                            {
                                availability.otherPartners.map((partner) => (
                                    <ProductOtherPartnerAvailabilityCard
                                        key={partner.partnerName}
                                        partner={partner}
                                        price={partner.price}
                                        schedules={availability.schedules}
                                    />
                                ))
                            }
                        </Box>
                    </div>
            }
        </Col>
    )

    const hasNewPrice = useCallback((price, originalPrice) => price < originalPrice, []);
    const currentCustomerHasNewPrice = useMemo(() => hasNewPrice(currentCustomerPriceAndPriceLevel.price, currentCustomerPriceAndPriceLevel.originalPrice), [currentCustomerPriceAndPriceLevel, hasNewPrice]);

    const applicablePrices = useMemo(() => {
        const nonZeroPrices = prices?.filter(info => (info?.price ?? 0) !== 0) ?? [];
        if (nonZeroPrices.length === 0) {
            return prices ?? [];
        }
        return nonZeroPrices ?? [];
    }, [prices])

    const allPricesZero = useMemo(() => prices?.every(info => (info?.price ?? 0) === 0), [prices])

    const pricesSection = (
        <Col xs={12} {...!inLargeViews && {lg: 6, xl: 7}} className='mb-3 d-flex flex-column' id={ElementIds.partPrices}>
            {
                !!pricesLoading &&
                <div className='loading part-prices'>
                    <div/>
                    <div/>
                    <div/>
                    <div/>
                    <div/>
                </div>
            }
            {
                !pricesLoading && !customer &&
                <>
                    {
                        applicablePrices?.length > 0 ? (
                            allPricesZero
                                ? <div className={'look-up-single-price-levels'}>
                                    <Alert variant={'filled'} className='my-2' severity={'warning'}>
                                        <Typography variant={'body2'} fontWeight={500}>
                                            This part has prices, but all of them are zero.
                                        </Typography>
                                    </Alert>
                                </div>
                                : <div className={'look-up-single-price-levels'}>
                                    {
                                        applicablePrices?.length > 1 &&
                                        <IconButton
                                            color={"primary"}
                                            className={'slider-arrow prev'}
                                            onPressed={() => slidePriceLevels(-1)}
                                        >
                                            <ChevronLeft className={'icon small'}/>
                                        </IconButton>
                                    }
                                    {
                                        applicablePrices?.length === 1 &&
                                        <Card variant={'outlined'}
                                              sx={{padding: 2, backgroundColor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                                            <Typography variant={'body2'} fontWeight={500}>
                                                {applicablePrices?.[0].priceLevel?.title ?? 'Price'}:
                                            </Typography>
                                            <Typography variant={'body1'} fontWeight={600}>
                                                {formatMoney(applicablePrices?.[0]?.price ?? 0)}
                                            </Typography>
                                        </Card>
                                    }
                                    {
                                        applicablePrices?.length > 1 &&
                                        <ResponsiveSlider
                                            ref={priceLevelSliderRef}
                                            autoplay={false}
                                            infinite={false}
                                            fade={false}
                                            dots={false}
                                            centerMode={false}
                                            swipe={false}
                                            initialIndex={0}
                                            responsiveWidth={150}
                                            parentId={ElementIds.partPrices}
                                        >
                                            {
                                                applicablePrices?.map((info) => (
                                                    <div key={info.priceLevel?.id} className='part-prices d-flex flex-column justify-content-start align-items-center px-2 p-1'>
                                                        <Row className='no-gutters justify-content-start w-100 '>
                                                            <p className='px-2 pt-1 text-xs font-weight-semi-bold secondary-color-1 text-left'>
                                                                {info.priceLevel?.title ?? ''}
                                                            </p>
                                                        </Row>
                                                        <p className='py-2'>
                                                            {formatMoney(info?.price ?? 0)}
                                                        </p>
                                                    </div>
                                                ))
                                            }
                                        </ResponsiveSlider>
                                    }
                                    {
                                        applicablePrices?.length > 1 &&
                                        <IconButton
                                            color={"primary"}
                                            className={'slider-arrow next'}
                                            onPressed={() => slidePriceLevels(1)}
                                        >
                                            <ChevronRight className={'icon small'}/>
                                        </IconButton>
                                    }
                                </div>
                        ) : (
                            <p className='text-color-3 my-2 text-sm text-center'>
                                This part does not have any prices.
                            </p>
                        )
                    }
                    <CustomerAutocomplete
                        label={'Customer'}
                        className={'mt-3'}
                        onChange={changeCustomer}
                    />
                </>
            }
            {
                !pricesLoading && !!customer &&
                <>
                    <Card
                        variant='outlined'
                        sx={{
                            background: 'background.tertiary',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 2,
                            gap: 2,
                            marginBlock: 2,
                        }}
                    >
                        <IconButton onPressed={removeCustomer} color={'paper.error'}>
                            <RemoveIcon className={'icon medium'}/>
                        </IconButton>
                        <Typography variant={'body2'} fontWeight={600}>
                            {customer?.name ?? ""}
                        </Typography>
                        {
                            (loadingCurrentCustomerPrice) &&
                            <Skeleton width={60} height={40} sx={{marginInlineStart: 'auto',}}/>
                        }
                        {
                            !(loadingCurrentCustomerPrice) &&
                            <Box sx={{display: 'flex', justifyContent: 'center', gap: 1, alignItems: 'flex-end', marginInlineStart: 'auto', flexDirection: 'column'}}>
                                {
                                    currentCustomerHasNewPrice &&
                                    <Typography variant={'body2'} fontWeight={500}>
                                        {formatMoney(currentCustomerPriceAndPriceLevel.price)} ({currentCustomerPriceAndPriceLevel.priceLevelTitle})
                                    </Typography>
                                }
                                {
                                    currentCustomerPriceAndPriceLevel.price <= 0
                                        ? <Typography variant={'body2'} fontWeight={500}>
                                            {formatMoney(0)}
                                        </Typography>
                                        : <Typography
                                            variant={'body2'}
                                            sx={{
                                                ...(currentCustomerHasNewPrice && {
                                                    textDecoration: 'line-through',
                                                    color: 'text.disabled',
                                                }),
                                            }}
                                        >
                                            {formatMoney(currentCustomerPriceAndPriceLevel.originalPrice)}
                                        </Typography>
                                }
                            </Box>
                        }
                    </Card>
                    <Box sx={{position: 'relative', marginBlock: 2}}>
                        <Divider absolute sx={{
                            borderStyle: "dashed",
                            borderColor: 'text.disabled',
                            borderBottomWidth: 2,
                        }}/>
                        <Typography
                            variant={'body2'}
                            color={'tertiary.main'}
                            fontWeight={600}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -65%)',
                                backgroundColor: 'background.paper',
                                paddingInline: 2,
                            }}
                        >
                            Compare Prices
                        </Typography>
                    </Box>
                    {
                        compareCustomers.map((compareCustomer) => {
                            const customerHasNewPrice = hasNewPrice(compareCustomer.price, compareCustomer.originalPrice);
                            return (
                                <Card
                                    key={compareCustomer.customerId}
                                    variant='outlined'
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: 1,
                                        gap: 2,
                                        marginBlock: 1,
                                    }}
                                >
                                    <IconButton
                                        onPressed={() => setCompareCustomers(prevState => prevState.filter(e => e.customerId !== compareCustomer.customerId))}
                                        color={'paper.error'}
                                    >
                                        <Remove className={'icon small'}/>
                                    </IconButton>
                                    <Typography variant={'body2'} fontWeight={500}>
                                        {compareCustomer?.customerName}
                                    </Typography>
                                    <Box sx={{display: 'flex', justifyContent: 'center', gap: 1, alignItems: 'flex-end', marginInlineStart: 'auto', flexDirection: 'column'}}>
                                        {
                                            customerHasNewPrice &&
                                            <Typography variant={'body2'} fontWeight={500}>
                                                {formatMoney(compareCustomer?.price)} ({compareCustomer?.priceLevelTitle})
                                            </Typography>
                                        }
                                        {
                                            compareCustomer?.price <= 0
                                                ? <Typography variant={'body2'} fontWeight={500}></Typography>
                                                : <Typography
                                                    variant={'body2'}
                                                    sx={{
                                                        ...(customerHasNewPrice && {
                                                            textDecoration: 'line-through',
                                                            color: 'text.disabled',
                                                        })
                                                    }}
                                                >
                                                    {formatMoney(compareCustomer?.originalPrice)}
                                                </Typography>
                                        }
                                    </Box>
                                </Card>
                            );
                        })
                    }
                    {
                        addingCompareCustomer &&
                        <div className='loading small my-2'>
                            <div/>
                        </div>
                    }
                    <CustomerAutocomplete
                        label={'Customer'}
                        className={'mt-3'}
                        onChange={getCompareCustomerPrice}
                        value={null}
                        filterOptions={(options) => options.filter(option => customer.id !== option.id && !compareCustomers.some(e => e.customerId === option.id))}
                    />
                </>
            }
        </Col>
    );

    return (
        <>
            <Row id={id} className={`${className} no-gutters w-100`}>
                {
                    !part && !partLoading
                        ? <TryAgain
                            text={"There was a problem with getting this products' information."}
                            buttonText={'Try Again'}
                            onClick={fetchPartInformation}
                        />
                        : inLargeViews
                            ? <>
                                <Col xs={6} className={'d-flex flex-wrap'} style={{height: 'fit-content'}}>
                                    {imageSection}
                                    {
                                        !!customer &&
                                        <>
                                            <div className={'w-100 my-3'}/>
                                            <CustomerAccountingAndSearchesDetails
                                                customer={customer}
                                                showAccounting
                                            />
                                        </>
                                    }
                                    <div className={'w-100 my-3'}/>
                                    {
                                        operatorAccessLevels.packages.sales.pricesInLookup &&
                                        operatorAccessLevels.packages.base.customerList &&
                                        pricesSection
                                    }
                                </Col>
                                <Col xs={6} className={'d-flex flex-wrap'} style={{height: 'fit-content'}}>
                                    {
                                        basicInfoSection
                                    }
                                    {
                                        operatorAccessLevels.packages.lookup.productVariants &&
                                        variantsSection
                                    }
                                    {
                                        operatorAccessLevels.packages.bizInventory.availabilityInLookup &&
                                        availabilitySection
                                    }
                                    {
                                        operatorAccessLevels.packages.sales.submitQuoteInLookup &&
                                        !!customer?.id &&
                                        <>
                                            <div className={'w-100 my-3'}/>
                                            <AddToQuote
                                                partLoading={partLoading}
                                                availabilityLoading={availabilityLoading}
                                                part={part}
                                                customer={customer}
                                                query={query}
                                                warehouses={availability?.warehouses}
                                                getAvailability={getPartAvailability}
                                                accessedWarehouses={accessedWarehouses}
                                            />
                                        </>
                                    }
                                </Col>
                            </>
                            : <>
                                {
                                    imageSection
                                }
                                {
                                    basicInfoSection
                                }
                                {
                                    operatorAccessLevels.packages.lookup.productVariants &&
                                    variantsSection
                                }
                                {
                                    operatorAccessLevels.packages.bizInventory.availabilityInLookup &&
                                    availabilitySection
                                }
                                {
                                    operatorAccessLevels.packages.sales.pricesInLookup &&
                                    operatorAccessLevels.packages.base.customerList &&
                                    !!customer &&
                                    <>
                                        <div className={'w-100 my-3'}/>
                                        <CustomerAccountingAndSearchesDetails
                                            customer={customer}
                                            showAccounting
                                        />
                                    </>
                                }
                                <div className={'w-100 my-3'}/>
                                {
                                    operatorAccessLevels.packages.sales.pricesInLookup &&
                                    operatorAccessLevels.packages.base.customerList &&
                                    pricesSection
                                }
                                {
                                    operatorAccessLevels.packages.sales.submitQuoteInLookup &&
                                    !!customer?.id &&
                                    <AddToQuote
                                        partLoading={partLoading}
                                        availabilityLoading={availabilityLoading}
                                        part={part}
                                        customer={customer}
                                        query={query}
                                        warehouses={availability?.warehouses}
                                        getAvailability={getPartAvailability}
                                        accessedWarehouses={accessedWarehouses}
                                    />
                                }
                            </>

                }
            </Row>
            {
                operatorAccessLevels.packages.sales.changeProductPricesInLookup &&
                <PartPricesModal
                    open={showPrices}
                    setOpen={setShowPrices}
                    partId={part?.id}
                    refreshAvailabilities={getPartPrices}
                />
            }
            <ImagesPreviewModal
                images={previewImages.images}
                open={previewImages.open}
                setOpen={() => setPreviewImages({open: false, images: []})}
            />
        </>
    );
}


const AddToQuote = ({partLoading, availabilityLoading, part, customer, query, warehouses: _warehouses, getAvailability, accessedWarehouses: _accessedWarehouses}) => {
    const operatorAccessLevels = useAccessLevels();
    const shouldCheckFormQualification = useMemo(
        () => !!part?.id && !!customer?.id,
        [customer?.id, part?.id],
    );
    const {
        loading: formQualificationLoading,
        error: formQualificationError,
        errorMessage: formQualificationErrorMessage,
        isCustomerEmailError: formQualificationCustomerEmailError,
        qualified: formQualificationQualified,
        qualification: formQualification,
        refresh: refreshFormQualification,
    } = usePartFormQualification({
        partId: part?.id,
        customerId: customer?.id,
        enabled: shouldCheckFormQualification,
    });
    const {checking: checkingFormQualification, ensureQualified, GateDialogs} = usePartFormQualificationGate();
    const [quantity, setQuantity] = useState(1);
    const [showAddToQuote, setShowAddToQuote] = useState(false);
    const [warehousesPopoverAnchor, setWarehousesPopoverAnchor] = useState(null);
    const [creatingItemInSelectedWarehouse, setCreatingItemInSelectedWarehouse] = useState(false);
    const isMounted = useIsMounted();

    const accessedWarehouses = useMemo(() => EnvService.enableDemoNameMasking
        ? _accessedWarehouses.map(warehouse => ({
            ...warehouse,
            name: generateWarehouseName(warehouse.name)
        }))
        : _accessedWarehouses, [_accessedWarehouses]);

    const warehouses = useMemo(() => EnvService.enableDemoNameMasking
        ? _warehouses?.map(warehouse => ({
            ...warehouse,
            localWarehouse: {
                ...warehouse.localWarehouse,
                warehouseName: generateWarehouseName(warehouse.localWarehouse?.warehouseName)
            }
        }))
        : _warehouses, [_warehouses]);

    const defaultWarehouse = useMemo(() => (query?.warehouseCode ? accessedWarehouses.find(e => e.code === query?.warehouseCode) : accessedWarehouses.find(e => e.code === '00')) ?? accessedWarehouses[0], [query?.warehouseCode, accessedWarehouses]);
    const [warehouse, setWarehouse] = useState(defaultWarehouse);
    const localWarehouse = useMemo(() => warehouses?.find(e => e.localWarehouse?.warehouseCode === warehouse?.code)?.localWarehouse, [warehouses, warehouse?.code])

    /**
     * Resets the warehouse to the default warehouse when the default warehouse changes
     */
    useEffect(() => {
        setWarehouse(defaultWarehouse);
    }, [defaultWarehouse])

    /**
     * Verifies form qualification, then opens the add to quote modal.
     */
    const addToQuote = useCallback(async () => {
        if (formQualificationLoading || formQualificationError) {
            return;
        }
        if (shouldCheckFormQualification && !formQualificationQualified) {
            const canProceed = await ensureQualified({
                partId: part?.id,
                customerId: customer?.id,
                partLabel: part?.partNo,
            });
            if (canProceed) {
                setShowAddToQuote(true);
            }
            return;
        }
        setShowAddToQuote(true);
    }, [
        customer?.id,
        ensureQualified,
        formQualificationError,
        formQualificationLoading,
        formQualificationQualified,
        part?.id,
        part?.partNo,
        shouldCheckFormQualification,
    ]);

    const addToQuoteDisabled = useMemo(() => (
        (!!partLoading && !!availabilityLoading)
        || !quantity
        || !customer?.id
        || checkingFormQualification
        || (shouldCheckFormQualification && (formQualificationLoading || formQualificationError))
    ), [
        availabilityLoading,
        checkingFormQualification,
        customer?.id,
        formQualificationError,
        formQualificationLoading,
        partLoading,
        quantity,
        shouldCheckFormQualification,
    ]);

    /**
     * Creates the item in the selected warehouse
     * @type {(function(): Promise<void>)}
     */
    const createItemInSelectedWarehouse = useCallback(async () => {
        if (!localWarehouse || !localWarehouse?.isComingSoon || !warehouse)
            return;
        setCreatingItemInSelectedWarehouse(true);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [part?.partNo],
        });
        if (!isMounted())
            return;
        if (!partInfoResponse?.resultFlag) {
            setCreatingItemInSelectedWarehouse(false);
            return;
        }
        const forApi = {
            partId: partInfoResponse.data?.find(e => e.partNo === part?.partNo)?.id,
            warehouseId: warehouse?.id,
        }
        const response = await Api.createPartInWarehouse(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await getAvailability();
        }
        if (!isMounted())
            return;
        setCreatingItemInSelectedWarehouse(false)
    }, [isMounted, localWarehouse, warehouse, part?.partNo, getAvailability]);

    /**
     * Resets the quantity to 1
     * @type {(function(): void)}
     */
    const resetValues = useCallback(() => {
        setQuantity(1);
    }, []);

    return (
        <>
            <Col xs={12} className='d-flex justify-content-center mb-3'>
                <Card
                    variant={'outlined'}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 2,
                        gap: 1,
                        height: 'fit-content',
                        width: '100%'
                    }}
                >
                    {
                        operatorAccessLevels.packages.sales.manageWarehousesInQuote && !!accessedWarehouses.length &&
                        <Button
                            disabled={!accessedWarehouses.length}
                            sx={{backgroundColor: 'background.default', height: 37, minWidth: 37, color: 'primary.main'}}
                            onClickCapture={(e) => setWarehousesPopoverAnchor(e.currentTarget)}
                        >
                            Warehouse: {localWarehouse?.warehouseName ?? '--'} ({localWarehouse?.warehouseCode ?? '--'})
                        </Button>
                    }
                    {
                        !!localWarehouse && !!accessedWarehouses.length && !!localWarehouse?.isComingSoon &&
                        <Button
                            onClick={() => createItemInSelectedWarehouse()}
                            disabled={creatingItemInSelectedWarehouse}
                            variant={'outlined'}
                            color={'primary'}
                        >
                            {
                                creatingItemInSelectedWarehouse
                                    ? "Creating in warehouse..."
                                    : "Create in warehouse"
                            }
                        </Button>
                    }
                    {
                        !accessedWarehouses.length &&
                        <Typography variant={'body2'} color={'error'} fontWeight={600} textAlign={'center'}>
                            You do not have access to any warehouses. Please contact an administrator.
                        </Typography>
                    }
                    {
                        !localWarehouse?.isComingSoon && !!accessedWarehouses.length &&
                        (
                            shouldCheckFormQualification && formQualificationLoading
                                ? <PartFormQualificationLoadingIndicator/>
                                : shouldCheckFormQualification && formQualificationError
                                    ? <Box sx={{width: '100%'}}>
                                        <PartFormQualificationErrorIndicator
                                            onRetry={() => void refreshFormQualification()}
                                            errorMessage={formQualificationErrorMessage}
                                            customerId={customer?.id}
                                            showCustomerProfileButton={formQualificationCustomerEmailError}
                                        />
                                    </Box>
                                    : shouldCheckFormQualification && !formQualificationQualified
                                        ? (
                                            <PartFormComplianceCard
                                                forms={formQualification?.forms ?? []}
                                                showPartLabels={false}
                                                onQualificationRefresh={refreshFormQualification}
                                            />
                                        )
                                        : (
                                            <>
                                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center'}}>
                                                    <IconButton
                                                        className={''}
                                                        onPressed={() => setQuantity(prevState => Math.max((+prevState ?? 0) - 1, 0))}
                                                        color={'secondary'}
                                                    >
                                                        <Remove className={'icon small'}/>
                                                    </IconButton>
                                                    <div className={'quantity-field'}>
                                                        <TextField
                                                            size={'small'}
                                                            fullWidth
                                                            inputProps={{min: -9999, max: 9999}}
                                                            type={'number'}
                                                            placeholder={''}
                                                            sx={{
                                                                '& input': {
                                                                    textAlign: 'center',
                                                                }
                                                            }}
                                                            value={quantity.toString()}
                                                            onChange={(e) => setQuantity(!e.target.value ? e.target.value : Number(e.target.value))}
                                                        />
                                                    </div>
                                                    <IconButton
                                                        onPressed={() => setQuantity(prevState => (+prevState ?? 0) + 1)}
                                                        color={'secondary'}
                                                    >
                                                        <Add className={'icon small'}/>
                                                    </IconButton>
                                                </Box>
                                                <Box sx={{display: 'flex', justifyContent: 'center', padding: 2, gap: 2}}>
                                                    <Button
                                                        disabled={addToQuoteDisabled}
                                                        onClick={() => void addToQuote()}
                                                        variant={'contained'}
                                                        color={'primary'}
                                                    >
                                                        {partLoading || availabilityLoading || checkingFormQualification || formQualificationLoading
                                                            ? 'Loading...'
                                                            : 'Add to Quote'}
                                                    </Button>
                                                </Box>
                                            </>
                                        )
                        )
                    }

                </Card>
            </Col>
            {
                operatorAccessLevels.packages.sales.submitQuoteInLookup &&
                <AddToQuoteModal
                    partId={part?.id}
                    partNumber={part?.partNo}
                    customerId={customer?.id}
                    warehouseCode={warehouse?.code}
                    customerName={customer?.name ?? "Selected Customer"}
                    quantity={quantity}
                    resetValues={resetValues}
                    open={showAddToQuote}
                    setOpen={setShowAddToQuote}
                    searchToken={query.searchToken}
                />
            }
            {GateDialogs}
            <Popover
                open={!!warehousesPopoverAnchor}
                anchorEl={warehousesPopoverAnchor}
                elevation={1}
                onClose={() => setWarehousesPopoverAnchor(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: 200,
                        padding: 1,
                    }}
                >
                    <Typography variant={'body2'} fontWeight={600} sx={{marginBlockEnd: 1}}>
                        Select your Warehouse
                    </Typography>
                    {
                        accessedWarehouses?.map((e) => (
                            <MenuItem
                                key={e.code}
                                value={e}
                                selected={localWarehouse?.warehouseCode === e?.code}
                                onClick={() => {
                                    setWarehousesPopoverAnchor(null);
                                    setWarehouse(e);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: 'fill-available',
                                    borderRadius: 1,
                                    marginInline: 0,
                                    padding: 1,
                                    '.fill': {
                                        color: 'primary.main',
                                    }
                                }}
                            >
                                <Typography variant={'caption'}>
                                    {e.name ?? ""} <Typography
                                    sx={{display: 'inline-block'}} variant={'body2'} color={'primary'} fontWeight={600}
                                >
                                    ({e.code})
                                </Typography>
                                </Typography>
                            </MenuItem>
                        ))
                    }
                </Box>
            </Popover>
        </>
    )
}

export default LookupProductInformationViewBasicSection;
